import type { mat4 } from 'gl-matrix';

import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';

import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkLineSource from '@kitware/vtk.js/Filters/Sources/LineSource';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';

import { transform2dTo3d } from '@/src/core/contours/referencePlane';

type Vec3 = [number, number, number];

interface ActorOpts {
  color?: Vec3;
  opacity?: number;
  lineWidth?: number;
  polygonOffset?: [number, number];
}

interface DicomImageInfo {
  ImageOrientationPatient: number[];
  ImagePositionPatient: number[];
  PixelSpacing: number[];
  Rows: number;
  Columns: number;
}
interface LocalizerRenderer {
  getActiveCamera: () => { getParallelProjection: () => boolean };
  addActor: (actor: vtkActor) => void;
  removeActor: (actor: vtkActor) => void;
}
interface LocalizerViewProxy {
  getRenderer: () => LocalizerRenderer | null | undefined;
}
interface LocalizerResult {
  localizerPoints: number[][] | null;
  intersectionLinePoints: number[][] | null;
}

/** Minimal actor factory (was actor-builders.makeActor, trimmed to line needs). */
function makeActor(
  mapperInput: vtkPolyData | vtkLineSource,
  opts: ActorOpts = {},
): vtkActor {
  const { color, opacity = 1, lineWidth, polygonOffset } = opts;

  const mapper = vtkMapper.newInstance();
  if ('getOutputPort' in mapperInput) {
    mapper.setInputConnection(mapperInput.getOutputPort());
  }
  else {
    mapper.setInputData(mapperInput);
  }

  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);

  const prop = actor.getProperty();
  if (color) prop.setColor(color[0], color[1], color[2]);
  prop.setOpacity(opacity);
  if (lineWidth != null) prop.setLineWidth(lineWidth);
  actor.setVisibility(true);

  if (polygonOffset) {
    mapper.setResolveCoincidentTopologyToPolygonOffset();
    mapper.setResolveCoincidentTopologyPolygonOffsetParameters(
      polygonOffset[0],
      polygonOffset[1],
    );
  }

  return actor;
}

function createLineSegmentActor(p1: Vec3, p2: Vec3, opts: ActorOpts = {}): vtkActor | null {
  const eps = 1e-6;
  if (Math.abs(p1[0] - p2[0]) < eps && Math.abs(p1[1] - p2[1]) < eps && Math.abs(p1[2] - p2[2]) < eps) {
    return null;
  }
  const lineSource = vtkLineSource.newInstance({ point1: p1, point2: p2 });
  return makeActor(lineSource, { polygonOffset: [-1, -1], ...opts });
}

function createCompoundLineActor(segments: [Vec3, Vec3][], opts: ActorOpts = {}): vtkActor | null {
  if (!segments.length) return null;
  const polyData = vtkPolyData.newInstance();
  const points = vtkPoints.newInstance();
  const pointData = new Float32Array(segments.length * 2 * 3);
  const linesData: number[] = [];
  let idx = 0;
  segments.forEach(([a, b]) => {
    pointData.set(a, idx * 3);
    pointData.set(b, (idx + 1) * 3);
    linesData.push(2, idx, idx + 1);
    idx += 2;
  });
  points.setData(pointData);
  polyData.setPoints(points);
  const lines = vtkCellArray.newInstance();
  lines.setData(new Uint32Array(linesData));
  polyData.setLines(lines);
  return makeActor(polyData, { polygonOffset: [-1, -1], ...opts });
}

/** Break a segment into dash sub-segments (world units). */
function buildDashSegments(p1: Vec3, p2: Vec3, dashWorld = 1, gapWorld = 0.8): [Vec3, Vec3][] {
  const dir = [p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]];
  const len = Math.hypot(dir[0], dir[1], dir[2]);
  if (len < 1e-6) return [];
  const u = [dir[0] / len, dir[1] / len, dir[2] / len];
  const step = dashWorld + gapWorld;
  const segs: [Vec3, Vec3][] = [];
  for (let d = 0; d < len; d += step) {
    const a = d;
    const b = Math.min(d + dashWorld, len);
    segs.push([
      [p1[0] + u[0] * a, p1[1] + u[1] * a, p1[2] + u[2] * a],
      [p1[0] + u[0] * b, p1[1] + u[1] * b, p1[2] + u[2] * b],
    ]);
  }
  return segs;
}

export function addLocalizerActors(
  viewProxy: LocalizerViewProxy,
  imageInfoDicom: DicomImageInfo,
  imageInfoDicomRef: DicomImageInfo,
  indexToWorld: mat4,
  isActive: boolean,
  showOutline = false,
): vtkActor[] {
  const actors: vtkActor[] = [];

  if (!viewProxy || !imageInfoDicom || !imageInfoDicomRef) return actors;

  const renderer = viewProxy.getRenderer();
  if (!renderer) {
    console.warn('Renderer instance is required to add localizer actors.');
    return actors;
  }

  const camera = renderer.getActiveCamera();
  if (!camera.getParallelProjection()) {
    console.warn('Camera is not in parallel projection mode.');
    return actors;
  }

  const { intersectionLinePoints, localizerPoints } = calculateLocalizer(
    imageInfoDicom.ImageOrientationPatient,
    imageInfoDicom.ImagePositionPatient,
    imageInfoDicom.PixelSpacing[0],
    imageInfoDicom.PixelSpacing[1],
    imageInfoDicom.Rows,
    imageInfoDicom.Columns,
    imageInfoDicomRef.ImageOrientationPatient,
    imageInfoDicomRef.ImagePositionPatient,
    imageInfoDicomRef.PixelSpacing[0],
    imageInfoDicomRef.PixelSpacing[1],
    imageInfoDicomRef.Rows,
    imageInfoDicomRef.Columns,
  );

  function image2DCordinateToWorld(x: number, y: number) {
    return transform2dTo3d([x, y], { t: indexToWorld, _tInv: null });
  }

  if (intersectionLinePoints) {
    const worldPoint1 = image2DCordinateToWorld(
      intersectionLinePoints[0][0],
      intersectionLinePoints[0][1],
    );
    const worldPoint2 = image2DCordinateToWorld(
      intersectionLinePoints[1][0],
      intersectionLinePoints[1][1],
    );
    const actor = createLineSegmentActor(worldPoint1, worldPoint2, {
      color: isActive ? [0, 1, 0] : [0, 0, 1],
      opacity: 0.8,
      lineWidth: 3,
    });
    if (actor) actors.push(actor);
  }

  if (showOutline && localizerPoints) {
    const corners = localizerPoints.map(([x, y]) => image2DCordinateToWorld(x, y));
    const segments: [Vec3, Vec3][] = [];
    for (let i = 0; i < corners.length; i += 1) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      segments.push(...buildDashSegments(a, b, 1, 0.8));
    }
    const outline = createCompoundLineActor(segments, {
      color: isActive ? [0, 1, 0] : [0, 0, 1],
      opacity: 0.5,
      lineWidth: 1.5,
    });
    if (outline) actors.push(outline);
  }

  // actually add them to the scene
  actors.forEach(actor => renderer.addActor(actor));
  return actors;
}

export function removeActors(
  viewProxy: LocalizerViewProxy,
  actors: vtkActor[],
) {
  const renderer = viewProxy?.getRenderer();
  if (!renderer || !actors) return;
  actors.forEach((actor) => {
    if (!actor) return;
    renderer.removeActor(actor);
    actor.getMapper()?.delete();
    actor.delete();
  });
}

export function calculateLocalizer(
  iop: number[],
  ipp: number[],
  pxHeight: number,
  pxWidth: number,
  imgHeight: number,
  imgWidth: number,
  iopRef: number[],
  ippRef: number[],
  pxHeightRef: number,
  pxWidthRef: number,
  imgHeightRef: number,
  imgWidthRef: number,
): LocalizerResult {
  // adapted from David Clunie
  // https://www.dclunie.com/medical-image-faq/html/part2.html
  if (
    !Array.isArray(iop)
    || iop.length !== 6
    || !iop.every(num => typeof num === 'number')
  ) {
    console.warn('iop must be an array of six numbers.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (
    !Array.isArray(iopRef)
    || iopRef.length !== 6
    || !iopRef.every(num => typeof num === 'number')
  ) {
    console.warn('iopRef must be an array of six numbers.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (
    !Array.isArray(ipp)
    || ipp.length !== 3
    || !ipp.every(num => typeof num === 'number')
  ) {
    console.warn('ipp must be an array of three numbers.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (
    !Array.isArray(ippRef)
    || ippRef.length !== 3
    || !ippRef.every(num => typeof num === 'number')
  ) {
    console.warn('ippRef must be an array of three numbers.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (pxHeight <= 0) {
    console.warn('pxHeight must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (pxWidth <= 0) {
    console.warn('pxWidth must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (pxHeightRef <= 0) {
    console.warn('pxHeightRef must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (pxWidthRef <= 0) {
    console.warn('pxWidthRef must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (imgHeight <= 0) {
    console.warn('imgHeight must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (imgWidth <= 0) {
    console.warn('imgWidth must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (imgHeightRef <= 0) {
    console.warn('imgHeightRef must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  if (imgWidthRef <= 0) {
    console.warn('imgWidthRef must be greater than 0.');
    return { localizerPoints: null, intersectionLinePoints: null };
  }

  const dst_row_dircos_x = iop[0];
  const dst_row_dircos_y = iop[1];
  const dst_row_dircos_z = iop[2];
  const dst_col_dircos_x = iop[3];
  const dst_col_dircos_y = iop[4];
  const dst_col_dircos_z = iop[5];
  const dst_pos_x = ipp[0];
  const dst_pos_y = ipp[1];
  const dst_pos_z = ipp[2];
  const dst_row_spacing = pxHeight;
  const dst_col_spacing = pxWidth;
  const dst_rows = imgHeight;
  const dst_cols = imgWidth;
  const dst_row_length = dst_cols * dst_row_spacing;
  const dst_col_length = dst_rows * dst_col_spacing;

  const src_row_dircos_x = iopRef[0];
  const src_row_dircos_y = iopRef[1];
  const src_row_dircos_z = iopRef[2];
  const src_col_dircos_x = iopRef[3];
  const src_col_dircos_y = iopRef[4];
  const src_col_dircos_z = iopRef[5];
  const src_pos_x = ippRef[0];
  const src_pos_y = ippRef[1];
  const src_pos_z = ippRef[2];
  const src_row_spacing = pxHeightRef;
  const src_col_spacing = pxWidthRef;
  const src_rows = imgHeightRef;
  const src_cols = imgWidthRef;
  const src_row_length = src_cols * src_row_spacing;
  const src_col_length = src_rows * src_col_spacing;

  const dst_nrm_dircos_x
    = dst_row_dircos_y * dst_col_dircos_z - dst_row_dircos_z * dst_col_dircos_y;
  const dst_nrm_dircos_y
    = dst_row_dircos_z * dst_col_dircos_x - dst_row_dircos_x * dst_col_dircos_z;
  const dst_nrm_dircos_z
    = dst_row_dircos_x * dst_col_dircos_y - dst_row_dircos_y * dst_col_dircos_x;

  const src_nrm_dircos_x
    = src_row_dircos_y * src_col_dircos_z - src_row_dircos_z * src_col_dircos_y;
  const src_nrm_dircos_y
    = src_row_dircos_z * src_col_dircos_x - src_row_dircos_x * src_col_dircos_z;
  const src_nrm_dircos_z
    = src_row_dircos_x * src_col_dircos_y - src_row_dircos_y * src_col_dircos_x;

  // Build a square to project with 4 corners TLHC, TRHC, BRHC, BLHC ...
  const pos_x = [0, 0, 0, 0];
  const pos_y = [0, 0, 0, 0];
  const pos_z = [0, 0, 0, 0];

  // TLHC is what is in ImagePositionPatient
  pos_x[0] = src_pos_x;
  pos_y[0] = src_pos_y;
  pos_z[0] = src_pos_z;

  // TRHC
  pos_x[1] = src_pos_x + src_row_dircos_x * src_row_length;
  pos_y[1] = src_pos_y + src_row_dircos_y * src_row_length;
  pos_z[1] = src_pos_z + src_row_dircos_z * src_row_length;

  // BRHC
  pos_x[2]
    = src_pos_x
      + src_row_dircos_x * src_row_length
      + src_col_dircos_x * src_col_length;
  pos_y[2]
    = src_pos_y
      + src_row_dircos_y * src_row_length
      + src_col_dircos_y * src_col_length;
  pos_z[2]
    = src_pos_z
      + src_row_dircos_z * src_row_length
      + src_col_dircos_z * src_col_length;

  // BLHC
  pos_x[3] = src_pos_x + src_col_dircos_x * src_col_length;
  pos_y[3] = src_pos_y + src_col_dircos_y * src_col_length;
  pos_z[3] = src_pos_z + src_col_dircos_z * src_col_length;

  const row_pixel = [0, 0, 0, 0];
  const col_pixel = [0, 0, 0, 0];

  const localizerPoints = [
    [0, 0],
    [0, 0],
    [0, 0],
    [0, 0],
  ];

  for (let i = 0; i < 4; ++i) {
    pos_x[i] -= dst_pos_x;
    pos_y[i] -= dst_pos_y;
    pos_z[i] -= dst_pos_z;

    // The rotation is easy ... just rotate by the row, col and normal
    // vectors ...
    const postmp_x
      = dst_row_dircos_x * pos_x[i]
        + dst_row_dircos_y * pos_y[i]
        + dst_row_dircos_z * pos_z[i];

    const postmp_y
      = dst_col_dircos_x * pos_x[i]
        + dst_col_dircos_y * pos_y[i]
        + dst_col_dircos_z * pos_z[i];

    const postmp_z
      = dst_nrm_dircos_x * pos_x[i]
        + dst_nrm_dircos_y * pos_y[i]
        + dst_nrm_dircos_z * pos_z[i];

    pos_x[i] = postmp_x;
    pos_y[i] = postmp_y;
    pos_z[i] = postmp_z;

    col_pixel[i] = pos_x[i] / dst_col_spacing + 0.5;
    row_pixel[i] = pos_y[i] / dst_row_spacing + 0.5;

    localizerPoints[i] = [col_pixel[i], row_pixel[i]];
  }

  // intersection
  // skip if parallel
  const parallel
    = dst_nrm_dircos_x * src_nrm_dircos_x
      + dst_nrm_dircos_y * src_nrm_dircos_y
      + dst_nrm_dircos_z * src_nrm_dircos_z;

  if (Math.abs(parallel) > 0.99999) {
    return { localizerPoints, intersectionLinePoints: null };
  }
  // direction of intersection line: a
  const a_x
    = dst_nrm_dircos_y * src_nrm_dircos_z - dst_nrm_dircos_z * src_nrm_dircos_y;
  const a_y
    = dst_nrm_dircos_z * src_nrm_dircos_x - dst_nrm_dircos_x * src_nrm_dircos_z;
  const a_z
    = dst_nrm_dircos_x * src_nrm_dircos_y - dst_nrm_dircos_y * src_nrm_dircos_x;

  // a x normal1: b
  const b_x = dst_nrm_dircos_y * a_z - dst_nrm_dircos_z * a_y;
  const b_y = dst_nrm_dircos_z * a_x - dst_nrm_dircos_x * a_z;
  const b_z = dst_nrm_dircos_x * a_y - dst_nrm_dircos_y * a_x;

  // pos1 - pos2, normal2
  let diffPosN2
    = (dst_pos_x - src_pos_x) * src_nrm_dircos_x
      + (dst_pos_y - src_pos_y) * src_nrm_dircos_y
      + (dst_pos_z - src_pos_z) * src_nrm_dircos_z;

  // normal2,b
  const n2b
    = src_nrm_dircos_x * b_x + src_nrm_dircos_y * b_y + src_nrm_dircos_z * b_z;

  // calc pos of intersection line
  const intersectionLine_x = dst_pos_x - (diffPosN2 / n2b) * b_x;
  const intersectionLine_y = dst_pos_y - (diffPosN2 / n2b) * b_y;
  const intersectionLine_z = dst_pos_z - (diffPosN2 / n2b) * b_z;

  // do the same for all sides
  const dst_pos_x2
    = dst_pos_x
      + dst_row_dircos_x * dst_row_length
      + dst_col_dircos_x * dst_col_length;
  const dst_pos_y2
    = dst_pos_y
      + dst_row_dircos_y * dst_row_length
      + dst_col_dircos_y * dst_col_length;
  const dst_pos_z2
    = dst_pos_z
      + dst_row_dircos_z * dst_row_length
      + dst_col_dircos_z * dst_col_length;

  const src_pos_x2
    = src_pos_x
      + src_row_dircos_x * src_row_length
      + src_col_dircos_x * src_col_length;
  const src_pos_y2
    = src_pos_y
      + src_row_dircos_y * src_row_length
      + src_col_dircos_y * src_col_length;
  const src_pos_z2
    = src_pos_z
      + src_row_dircos_z * src_row_length
      + src_col_dircos_z * src_col_length;

  diffPosN2
    = (dst_pos_x2 - src_pos_x2) * src_nrm_dircos_x
      + (dst_pos_y2 - src_pos_y2) * src_nrm_dircos_y
      + (dst_pos_z2 - src_pos_z2) * src_nrm_dircos_z;

  const intersectionLine_x2 = dst_pos_x2 - (diffPosN2 / n2b) * b_x;
  const intersectionLine_y2 = dst_pos_y2 - (diffPosN2 / n2b) * b_y;
  const intersectionLine_z2 = dst_pos_z2 - (diffPosN2 / n2b) * b_z;

  const dst_pos_x3 = dst_pos_x + dst_row_dircos_x * dst_row_length;
  const dst_pos_y3 = dst_pos_y + dst_row_dircos_y * dst_row_length;
  const dst_pos_z3 = dst_pos_z + dst_row_dircos_z * dst_row_length;

  const src_pos_x3 = src_pos_x + src_row_dircos_x * src_row_length;
  const src_pos_y3 = src_pos_y + src_row_dircos_y * src_row_length;
  const src_pos_z3 = src_pos_z + src_row_dircos_z * src_row_length;

  diffPosN2
    = (dst_pos_x3 - src_pos_x3) * src_nrm_dircos_x
      + (dst_pos_y3 - src_pos_y3) * src_nrm_dircos_y
      + (dst_pos_z3 - src_pos_z3) * src_nrm_dircos_z;

  const intersectionLine_x3 = dst_pos_x3 - (diffPosN2 / n2b) * b_x;
  const intersectionLine_y3 = dst_pos_y3 - (diffPosN2 / n2b) * b_y;
  const intersectionLine_z3 = dst_pos_z3 - (diffPosN2 / n2b) * b_z;

  const dst_pos_x4 = dst_pos_x + dst_col_dircos_x * dst_col_length;
  const dst_pos_y4 = dst_pos_y + dst_col_dircos_y * dst_col_length;
  const dst_pos_z4 = dst_pos_z + dst_col_dircos_z * dst_col_length;

  const src_pos_x4 = src_pos_x + src_col_dircos_x * src_col_length;
  const src_pos_y4 = src_pos_y + src_col_dircos_y * src_col_length;
  const src_pos_z4 = src_pos_z + src_col_dircos_z * src_col_length;

  diffPosN2
    = (dst_pos_x4 - src_pos_x4) * src_nrm_dircos_x
      + (dst_pos_y4 - src_pos_y4) * src_nrm_dircos_y
      + (dst_pos_z4 - src_pos_z4) * src_nrm_dircos_z;

  const intersectionLine_x4 = dst_pos_x4 - (diffPosN2 / n2b) * b_x;
  const intersectionLine_y4 = dst_pos_y4 - (diffPosN2 / n2b) * b_y;
  const intersectionLine_z4 = dst_pos_z4 - (diffPosN2 / n2b) * b_z;

  pos_x[0] = intersectionLine_x;
  pos_y[0] = intersectionLine_y;
  pos_z[0] = intersectionLine_z;

  // TRHC
  pos_x[1] = intersectionLine_x2;
  pos_y[1] = intersectionLine_y2;
  pos_z[1] = intersectionLine_z2;

  pos_x[2] = intersectionLine_x3;
  pos_y[2] = intersectionLine_y3;
  pos_z[2] = intersectionLine_z3;

  pos_x[3] = intersectionLine_x4;
  pos_y[3] = intersectionLine_y4;
  pos_z[3] = intersectionLine_z4;

  // draw line
  let minXPos = [Infinity, Infinity];
  let maxXPos = [-Infinity, -Infinity];
  let minYPos = [Infinity, Infinity];
  let maxYPos = [-Infinity, -Infinity];

  for (let i = 0; i < 4; ++i) {
    pos_x[i] -= dst_pos_x;
    pos_y[i] -= dst_pos_y;
    pos_z[i] -= dst_pos_z;

    const postmp_x
      = dst_row_dircos_x * pos_x[i]
        + dst_row_dircos_y * pos_y[i]
        + dst_row_dircos_z * pos_z[i];

    const postmp_y
      = dst_col_dircos_x * pos_x[i]
        + dst_col_dircos_y * pos_y[i]
        + dst_col_dircos_z * pos_z[i];

    const postmp_z
      = dst_nrm_dircos_x * pos_x[i]
        + dst_nrm_dircos_y * pos_y[i]
        + dst_nrm_dircos_z * pos_z[i];

    pos_x[i] = postmp_x;
    pos_y[i] = postmp_y;
    pos_z[i] = postmp_z;

    col_pixel[i] = pos_x[i] / dst_col_spacing + 0.5;
    row_pixel[i] = pos_y[i] / dst_row_spacing + 0.5;

    if (i === 0) {
      minXPos = [col_pixel[i], row_pixel[i]];
      maxXPos = [col_pixel[i], row_pixel[i]];
      minYPos = [col_pixel[i], row_pixel[i]];
      maxYPos = [col_pixel[i], row_pixel[i]];
    }
    else {
      if (minXPos[0] > col_pixel[i]) {
        minXPos = [col_pixel[i], row_pixel[i]];
      }
      if (minYPos[1] > row_pixel[i]) {
        minYPos = [col_pixel[i], row_pixel[i]];
      }
      if (maxXPos[0] < col_pixel[i]) {
        maxXPos = [col_pixel[i], row_pixel[i]];
      }
      if (maxYPos[1] < row_pixel[i]) {
        maxYPos = [col_pixel[i], row_pixel[i]];
      }
    }
  }

  const intersectionLinePoints = [minXPos, maxXPos, minYPos, maxYPos];
  return { localizerPoints, intersectionLinePoints };
}
